// shared/statutory-interest.js — Late Payment of Commercial Debts (Interest)
// Act 1998. Pure integer-pence functions: no imports outside shared/, no DOM, no
// node builtins, so either runtime can call them.
//
// Interest: simple (s.1(1)), daily, at 8% a year over the Bank of England base
// rate in force on the debt's REFERENCE DATE — the 30 June or 31 December
// before its interest starts (the 2002 Order, art. 4; shared/base-rate-history.js
// has the rates and the rule) — on the balance OUTSTANDING ON EACH DAY, from
// the day after the due date (s.4(2)). A part payment lowers the balance from
// the day after it arrives, so the part paid late still carries interest for
// the days it was late. Fixed compensation is banded on the size of the DEBT
// (the invoice total, or each stage of a payment plan, which is a debt of its
// own): under £1,000 → £40; £1,000 to under £10,000 → £70; £10,000+ → £100.
//
// Sendinvo works it out on sterling invoices to business clients only. The
// business half is the Act's: it covers commercial debts, where both sides act
// in the course of business. The sterling half is ours, not the Act's — the
// Act isn't limited to sterling, but a base rate plus 8% on a debt in another
// currency is a figure we'd rather not put in front of someone as the answer.
import { normaliseClientType } from "./client-type.js";
import { formatMoney } from "./currencies.js";
import { formatDocumentDate } from "./document-format.js";
import { cisDeductionNowOf, depositCisNowPence, depositTargetPence, instalmentTargetPence, instalmentsWithCisNow } from "./money.js";
import { STATUTORY_UPLIFT_PERCENT, statutoryRateFor } from "./base-rate-history.js";

export { STATUTORY_UPLIFT_PERCENT };

// Whether statutory interest can be claimed on an invoice at all. The invoice
// drawer asks this before it works out an amount or offers an interest invoice.
export function statutoryInterestApplies({ currency, clientType }) {
  return (currency || "GBP") === "GBP" && normaliseClientType(clientType) === "business";
}

// The Act's fixed-compensation bands, named so the marketing pages can quote
// them instead of typing the thresholds. These are UK LAW, not product policy —
// they must never be bound to a Stripe or product limit that happens to share a
// value (£10,000 is also the Pay by Bank ceiling; the two are unrelated).
export const STATUTORY_BAND_LOW_PENCE = 100000;   // £1,000
export const STATUTORY_BAND_HIGH_PENCE = 1000000; // £10,000
export const STATUTORY_COMPENSATION_PENCE = { under: 4000, mid: 7000, over: 10000 };

export function statutoryCompensationPence(debtPence) {
  const debt = Math.max(0, Math.round(Number(debtPence) || 0));
  if (debt <= 0) return 0;
  if (debt < STATUTORY_BAND_LOW_PENCE) return STATUTORY_COMPENSATION_PENCE.under;
  if (debt < STATUTORY_BAND_HIGH_PENCE) return STATUTORY_COMPENSATION_PENCE.mid;
  return STATUTORY_COMPENSATION_PENCE.over;
}

// ── Days ─────────────────────────────────────────────────────────────────────
// Every day here is a calendar day, counted as a whole number since 1970 in
// UTC, so neither the time of day nor the reader's timezone moves a count.

const DAY_MS = 86400000;
const DAY = /^(\d{4})-(\d{2})-(\d{2})$/;

function dayNumberOf(text) {
  const match = DAY.exec(String(text ?? "").slice(0, 10));
  if (!match) return NaN;
  return Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3])) / DAY_MS;
}

function dayText(number) {
  return new Date(number * DAY_MS).toISOString().slice(0, 10);
}

// A `Date` is read with LOCAL getters — the day it is where the reader is —
// and a "YYYY-MM-DD" is that day; anything else an instant, read the same way.
function localDayNumber(value) {
  if (typeof value === "string" && DAY.test(value)) return dayNumberOf(value);
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return NaN;
  return Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()) / DAY_MS;
}

// WHOSE DAY IT IS, said by every caller (1 November 2026): an invoice's
// interest runs to the company's today (`companyToday()` on the web, the
// iPhone's `CalendarDay.today()`), and a default of `new Date()` read with
// local getters was the reader's own day, a day's interest out for an owner
// abroad, and a different figure on the web from the iPhone's.
function requireToday(today) {
  if (today == null || today === "") throw new Error("Statutory interest needs today: the company's day, as \"YYYY-MM-DD\".");
}

// Whole days overdue: 0 on/before the due date, 1 on the day after, and so
// on. Dates compare as calendar days (parse to UTC midnight), so the time of
// day never shifts the count.
export function daysOverdue(dueDate, today) {
  requireToday(today);
  const due = dayNumberOf(dueDate);
  const now = localDayNumber(today);
  if (!Number.isFinite(due) || !Number.isFinite(now)) return 0;
  return Math.max(0, now - due);
}

// ── One debt ─────────────────────────────────────────────────────────────────

/**
 * Interest on one debt, day by day. `startPence` is what was owed on it before
 * anything was paid; `reductions` are what came off it, each on its `day`
 * (a payment counts from the day AFTER it arrives — the day it lands is still
 * a day it was owed — and a negative one, a refund, puts money back). A
 * reduction on or before the due date simply leaves less to run late.
 *
 * Returns { dueDate, interestFrom, referenceDate, baseRatePercent,
 * ratePercent, days, interestPence, compensationPence }: `days` the days in
 * (due date, today] on which something was owed, and compensation only when
 * something was still owed the day interest started.
 */
function accrueDebt({ dueDate, startPence, reductions = [], bandPence, today }) {
  const due = dayNumberOf(dueDate);
  const now = localDayNumber(today);
  const rate = Number.isFinite(due) ? statutoryRateFor(dayText(due + 1)) : null;
  const empty = {
    dueDate: Number.isFinite(due) ? dayText(due) : null,
    interestFrom: Number.isFinite(due) ? dayText(due + 1) : null,
    referenceDate: rate?.referenceDate ?? null,
    baseRatePercent: rate?.baseRatePercent ?? null,
    ratePercent: rate?.ratePercent ?? STATUTORY_UPLIFT_PERCENT,
    days: 0,
    interestPence: 0,
    compensationPence: 0
  };
  if (!rate || !Number.isFinite(now) || now <= due) return empty;

  // What came off on each day, then the balance walked forward from the day
  // interest starts. Simple interest: the pence owed on each day, added up,
  // times the rate, over 365 — once, rounded once.
  const byDay = new Map();
  let balance = Math.max(0, Math.round(Number(startPence) || 0));
  for (const { day, pence } of reductions) {
    const n = Number(day);
    const amount = Math.round(Number(pence) || 0);
    if (!amount) continue;
    if (!Number.isFinite(n) || n <= due) balance -= amount;
    else byDay.set(n, (byDay.get(n) || 0) + amount);
  }
  balance = Math.max(0, balance);
  const owedOnFirstDay = balance > 0;
  let penceDays = 0;
  let days = 0;
  for (let d = due + 1; d <= now; d += 1) {
    if (balance > 0) {
      penceDays += balance;
      days += 1;
    }
    // A payment on day d comes off after day d has accrued.
    if (byDay.has(d)) balance = Math.max(0, balance - byDay.get(d));
  }
  const basisPoints = Math.round(rate.ratePercent * 100);
  return {
    ...empty,
    days,
    interestPence: Math.round((penceDays * basisPoints) / (10000 * 365)),
    compensationPence: owedOnFirstDay ? statutoryCompensationPence(bandPence) : 0
  };
}

/**
 * Accrued statutory interest + compensation for ONE overdue debt, from what is
 * outstanding now. `payments` ([{ day | receivedOn | recordedAt, amountPence }]) are what
 * came off it, when: given them, the days before each payment carry the
 * balance as it stood then; without them, it is as though today's balance had
 * been owed throughout. `debtPence` is the debt's size, for the compensation
 * band. The rate is the one the law fixes for the debt (statutoryRateFor).
 *
 * Returns { days, interestPence, compensationPence, ratePercent,
 * baseRatePercent, referenceDate }.
 */
export function computeStatutoryInterest({ outstandingPence, debtPence, dueDate, payments = [], today }) {
  requireToday(today);
  const outstanding = Math.max(0, Math.round(Number(outstandingPence) || 0));
  const reductions = paymentReductions(payments);
  const paidSince = reductions.reduce((sum, r) => sum + r.pence, 0);
  const debt = accrueDebt({ dueDate, startPence: outstanding + paidSince, reductions, bandPence: debtPence, today });
  return {
    days: debt.days,
    interestPence: debt.interestPence,
    compensationPence: debt.compensationPence,
    ratePercent: debt.ratePercent,
    baseRatePercent: debt.baseRatePercent,
    referenceDate: debt.referenceDate
  };
}

// Payments as reductions: the day each arrived (the day it was received, as
// the API serves it since migration 192, else a day, or an instant read as
// the reader's own day) and its amount in pence, oldest first.
function paymentReductions(payments) {
  return (Array.isArray(payments) ? payments : [])
    .map((payment) => ({
      day: localDayNumber(payment?.day ?? payment?.receivedOn ?? payment?.date ?? payment?.recordedAt ?? payment?.createdAt),
      pence: Math.round(Number(payment?.amountPence) || 0)
    }))
    .filter((r) => r.pence !== 0)
    .sort((a, b) => (Number.isFinite(a.day) ? a.day : -Infinity) - (Number.isFinite(b.day) ? b.day : -Infinity));
}

// ── A whole invoice ──────────────────────────────────────────────────────────

/**
 * Statutory interest on an invoice, as the API serves it (`totalPence`,
 * `amountPaidPence`, `amountCreditedPence`, `cisDeductionAmountPence`,
 * `dueDate`, `instalments`, `depositAmountPence`, `depositCisDeductionPence`,
 * `depositDueDate`, `payments`, `invoiceNumber`).
 *
 * THE DEBTS. A plan's stages are each a debt of their own, due on its own date
 * (s.3(1): a debt to pay "the whole or any part of the contract price"); so are
 * a deposit with a due date of its own and the balance after it. Anything else
 * is one debt. Each is owed its target — its amount less its share of a CIS
 * deduction, which the contractor pays HMRC rather than the sender — and banded
 * for compensation on its amount before the deduction.
 *
 * WHAT CAME OFF, AND WHEN. A credit note corrects what was owed rather than
 * paying it, so it comes off from the start (a plan's oldest stages first, as
 * `allocateInstalments` has it; never a deposit, which only cash pays). Cash
 * comes off on the day it was recorded, oldest debt first, and a refund puts
 * it back on the newest it paid. Payments are `payments` if given, else the
 * invoice's own; any part of `amountPaidPence` they don't account for is
 * taken as paid at the start, so the figure can only err towards claiming
 * less.
 *
 * Returns { debts, days, interestPence, compensationPence, ratePercent,
 * sentence, lines }: `debts` the ones that ran late (each with its own rate,
 * days and figures), `days` and `ratePercent` the longest-running one's,
 * `sentence` what the invoice's interest section says, and `lines` the
 * interest invoice's lines ([{ description, amountPence }]).
 */
export function invoiceStatutoryInterest(invoice = {}, { payments, today } = {}) {
  requireToday(today);
  const n = (value) => Math.max(0, Math.round(Number(value) || 0));
  const totalPence = n(invoice.totalPence);
  // What stands of the deduction once credit notes took the CIS on credited
  // labour off it, which comes off from the start as their credit does
  // (ADR-031, amended).
  const cisPence = cisDeductionNowOf(invoice);
  const cisCreditedPence = n(invoice.cisCreditedPence);
  const creditedPence = n(invoice.amountCreditedPence);
  const paidPence = Math.round(Number(invoice.amountPaidPence) || 0);

  const stages = instalmentsWithCisNow(Array.isArray(invoice.instalments) ? invoice.instalments : [], cisCreditedPence)
    .filter((ins) => ins && ins.dueDate)
    .sort((a, b) => (Number(a.seq) || 0) - (Number(b.seq) || 0));
  const depositPence = n(invoice.depositAmountPence);

  let debts;
  if (stages.length) {
    debts = stages.map((ins, i) => ({
      label: ins.label ? String(ins.label) : `instalment ${i + 1}`,
      dueDate: ins.dueDate,
      targetPence: instalmentTargetPence(ins),
      bandPence: n(ins.amountPence),
      creditable: true
    }));
  } else if (depositPence > 0 && invoice.depositDueDate) {
    const depositTarget = depositTargetPence({
      depositAmountPence: depositPence,
      depositCisDeductionPence: depositCisNowPence({
        depositCisDeductionPence: invoice.depositCisDeductionPence,
        cisDeductionPence: invoice.cisDeductionAmountPence,
        cisCreditedPence
      })
    });
    debts = [
      { label: "the deposit", dueDate: invoice.depositDueDate, targetPence: depositTarget, bandPence: depositPence, creditable: false },
      {
        label: "the balance",
        dueDate: invoice.dueDate,
        targetPence: Math.max(0, totalPence - cisPence - depositTarget),
        bandPence: Math.max(0, totalPence - depositPence),
        creditable: true
      }
    ];
  } else {
    debts = [{ label: null, dueDate: invoice.dueDate, targetPence: Math.max(0, totalPence - cisPence), bandPence: totalPence, creditable: true }];
  }

  // Credit first, from the start: oldest creditable debt first, then the rest.
  const owed = debts.map((debt) => debt.targetPence);
  const credited = debts.map(() => 0);
  let creditLeft = creditedPence;
  const creditOrder = [...debts.keys()].sort((a, b) => Number(!debts[a].creditable) - Number(!debts[b].creditable));
  for (const i of creditOrder) {
    const take = Math.min(creditLeft, owed[i]);
    credited[i] = take;
    creditLeft -= take;
  }

  // Cash, as a running total on each day it moved, poured into the debts
  // oldest first; each debt's reductions are the steps in what it received.
  const events = paymentReductions(payments ?? invoice.payments);
  const unexplained = paidPence - events.reduce((sum, r) => sum + r.pence, 0);
  if (unexplained !== 0) events.unshift({ day: -Infinity, pence: unexplained });
  const room = owed.map((target, i) => target - credited[i]);
  const received = debts.map(() => 0);
  const reductions = debts.map(() => []);
  let cash = 0;
  for (const event of events) {
    cash += event.pence;
    let left = Math.max(0, cash);
    room.forEach((space, i) => {
      const now = Math.min(space, left);
      left -= now;
      if (now !== received[i]) reductions[i].push({ day: event.day, pence: now - received[i] });
      received[i] = now;
    });
  }

  const results = debts.map((debt, i) => ({
    label: debt.label,
    bandPence: debt.bandPence,
    ...accrueDebt({
      dueDate: debt.dueDate,
      startPence: owed[i],
      reductions: [{ day: -Infinity, pence: credited[i] }, ...reductions[i]],
      bandPence: debt.bandPence,
      today
    })
  }));
  const late = results.filter((debt) => debt.days > 0);
  const longest = late.reduce((best, debt) => (best && best.days >= debt.days ? best : debt), null);
  const interestPence = late.reduce((sum, debt) => sum + debt.interestPence, 0);
  const compensationPence = late.reduce((sum, debt) => sum + debt.compensationPence, 0);
  const claim = {
    debts: late,
    days: longest?.days ?? 0,
    interestPence,
    compensationPence,
    ratePercent: longest?.ratePercent ?? results[0]?.ratePercent ?? STATUTORY_UPLIFT_PERCENT,
    // More than one debt on the invoice, so a line names which it is for.
    split: debts.length > 1
  };
  return { ...claim, sentence: interestSentence(claim), lines: interestInvoiceLines(claim, invoice.invoiceNumber) };
}

// ── Words ────────────────────────────────────────────────────────────────────

const gbp = (pence) => formatMoney(pence / 100, "GBP");
const daysWords = (days) => `${days} ${days === 1 ? "day" : "days"}`;
// "11.75", "12.5", "12": the shortest digits, never padded.
const rateWords = (percent) => String(Math.round(Number(percent) * 100) / 100);

/**
 * What the invoice's interest section says: "52 days overdue at 11.75%:
 * interest to date £14.02 · compensation £70.00" for one debt; for a plan or
 * a deposit with more than one late, "2 payments late: interest to date …".
 */
export function interestSentence({ debts = [], days = 0, ratePercent, interestPence = 0, compensationPence = 0 } = {}) {
  const figures = `interest to date ${gbp(interestPence)} · compensation ${gbp(compensationPence)}`;
  if (debts.length > 1) return `${debts.length} payments late: ${figures}`;
  return `${daysWords(days)} overdue at ${rateWords(ratePercent)}%: ${figures}`;
}

/**
 * The interest invoice's lines. An invoice that is one debt: "Statutory
 * interest on INV-0139 (52 days at 11.75%)" and the compensation. One paid in
 * stages, or as a deposit and a balance: a line of interest for each stage
 * that ran late, naming it and its due date, each at its own rate and days,
 * then a compensation line for each that earns one.
 */
export function interestInvoiceLines({ debts = [], split = false } = {}, invoiceNumber = "") {
  const number = String(invoiceNumber || "").trim() || "the invoice";
  const act = "Late Payment of Commercial Debts (Interest) Act 1998";
  if (debts.length <= 1 && !split) {
    const debt = debts[0];
    if (!debt) return [];
    return [
      { description: `Statutory interest on ${number} (${daysWords(debt.days)} at ${rateWords(debt.ratePercent)}%)`, amountPence: debt.interestPence },
      { description: `Late payment compensation (${act})`, amountPence: debt.compensationPence }
    ];
  }
  const named = (debt) => `${debt.label}${debt.dueDate ? `, due ${formatDocumentDate(debt.dueDate)}` : ""}`;
  return [
    ...debts.map((debt) => ({
      description: `Statutory interest on ${number}, ${named(debt)} (${daysWords(debt.days)} at ${rateWords(debt.ratePercent)}%)`,
      amountPence: debt.interestPence
    })),
    ...debts
      .filter((debt) => debt.compensationPence > 0)
      .map((debt) => ({ description: `Late payment compensation on ${named(debt)} (${act})`, amountPence: debt.compensationPence }))
  ];
}

// ── Late payment terms on the invoice (#934) ─────────────────────────────────
// An opt-in company setting, off until switched on: a sterling invoice to a
// business client may state, in one line, what the Act lets its sender claim
// if it is paid late. Never on an individual's invoice or one in another
// currency, exactly as the interest itself (statutoryInterestApplies). Nor on
// one for nothing, which can't be paid late.
//
// A draft follows the setting as it stands; a sent invoice states what it
// went out with (`invoices.states_late_payment_terms`, written by the send).
export function latePaymentTermsApply({ enabled, currency, clientType, totalPence }) {
  return Boolean(enabled) && statutoryInterestApplies({ currency, clientType }) && Math.round(Number(totalPence) || 0) > 0;
}

// The line itself, with the fixed compensation for this invoice's amount. It
// says what MAY be claimed and nothing about what will be: whether to claim
// is the sender's call, and the client reads it before anything is late.
//
// AN INVOICE PAID IN STAGES (#854). The Act sets the fixed sum per "qualifying
// debt", by its size (s.5A), and a debt to pay "the whole or any part of the
// contract price" is one (s.3(1)); gov.uk's own guidance says "You can only
// charge the business once for each payment". So a payment plan's stages are
// debts of their own, each banded on its own amount, and the line names the
// sum for each instalment paid late rather than one sum on the whole total.
// `instalmentAmountsPence` is the plan's stage amounts; fewer than two is no
// plan, and the line reads as it always has.
export function latePaymentTermsSentence(totalPence, { instalmentAmountsPence = [] } = {}) {
  const stages = (Array.isArray(instalmentAmountsPence) ? instalmentAmountsPence : [])
    .map((amount) => Math.round(Number(amount) || 0))
    .filter((amount) => amount > 0);
  const act = "under the Late Payment of Commercial Debts (Interest) Act 1998.";
  const interest = `we may claim statutory interest at ${STATUTORY_UPLIFT_PERCENT}% a year above the Bank of England base rate`;
  if (stages.length >= 2) {
    return `If an instalment is paid late, ${interest}, and ${stageCompensationWords(stages)}, ${act}`;
  }
  const compensation = statutoryCompensationPence(totalPence) || STATUTORY_COMPENSATION_PENCE.under;
  return `If this invoice is paid late, ${interest}, and £${compensation / 100} fixed compensation, ${act}`;
}

// "£70 fixed compensation for each instalment paid late" when every stage is
// in one band; otherwise each band the stages fall in, largest first: "£70
// fixed compensation for each instalment of £1,000 or more paid late, and £40
// for each under £1,000".
function stageCompensationWords(stages) {
  const bands = [
    { pence: STATUTORY_COMPENSATION_PENCE.over, of: "of £10,000 or more" },
    { pence: STATUTORY_COMPENSATION_PENCE.mid, of: "of £1,000 to £9,999.99" },
    { pence: STATUTORY_COMPENSATION_PENCE.under, of: "under £1,000" }
  ].filter((band) => stages.some((amount) => statutoryCompensationPence(amount) === band.pence));
  if (bands.length === 1) return `£${bands[0].pence / 100} fixed compensation for each instalment paid late`;
  const [first, ...rest] = bands;
  const others = rest.map((band) => `£${band.pence / 100} for each ${band.of}`);
  const tail = others.length === 1 ? `and ${others[0]}` : `${others.slice(0, -1).join(", ")}, and ${others[others.length - 1]}`;
  return `£${first.pence / 100} fixed compensation for each instalment ${first.of} paid late, ${tail}`;
}

// What switching the setting on adds, said where it's switched: the web's
// drawer and the app's editor (DocumentsCopy.lateTermsExplained) say this.
export const LATE_PAYMENT_TERMS_NOTE = "Adds a line to sterling invoices to business clients, saying you may claim statutory interest and fixed compensation if one is paid late, under the Late Payment of Commercial Debts (Interest) Act 1998. Invoices to individuals never carry it.";
